import { useState, useEffect } from 'react';
import { Users, Activity, AlertTriangle, Settings, MessageSquare, BarChart3, Shield, Database, Bell, Mail, Phone, Ban, CheckCircle, XCircle, Search, Filter, Download, RefreshCw, Heart, Reply, Edit3, Trash2, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext.tsx';

import { supabase } from '../lib/supabaseClient.ts';

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  blockedUsers: number;
  totalNotifications: number;
  systemHealth: 'healthy' | 'warning' | 'critical';
}

interface User {
  id: string;
  display_name: string;
  email: string;
  phone: string;
  created_at: string;
  last_seen: string;
  status: 'active' | 'blocked';
  notification_preferences: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
}

interface SystemLog {
  id: string;
  level: 'info' | 'warning' | 'error';
  message: string;
  timestamp: string;
  user_id?: string;
}

export default function AdminDashboard({ user }: { user: any }) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    activeUsers: 0,
    blockedUsers: 0,
    totalNotifications: 0,
    systemHealth: 'healthy'
  });
  const [users, setUsers] = useState<User[]>([]);
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [bulkMessage, setBulkMessage] = useState('');
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkResult, setBulkResult] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showUserSelection, setShowUserSelection] = useState(false);
  const [logFilter, setLogFilter] = useState('all');
  const [messagesUnreadCount, setMessagesUnreadCount] = useState(0);
  const [messageCountInterval, setMessageCountInterval] = useState<NodeJS.Timeout | null>(null);
  const [userUnreadCounts, setUserUnreadCounts] = useState<Record<string, number>>({});
  const [selectedFarmer, setSelectedFarmer] = useState<string | null>(null);
  const [adminMessage, setAdminMessage] = useState('');
  const [adminMessages, setAdminMessages] = useState<any[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyingTo, setReplyingTo] = useState<any | null>(null);
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<string | null>(null);
  
  // Use exact same logic as SQL: raw_user_meta_data->>'full_name'
  const adminDisplayName = user?.user_metadata?.full_name || 'Admin';

  useEffect(() => {
    loadAdminData();
    loadUnreadMessageCount();
    loadUserUnreadCounts();
    
    // Set up real-time message count polling
    const interval = setInterval(() => {
      loadUnreadMessageCount();
      loadUserUnreadCounts();
    }, 5000);
    setMessageCountInterval(interval);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  const loadUnreadMessageCount = async () => {
    if (!user?.id) return;
    
    try {
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('receiver_id', user.id)
        .is('read_at', null);
      
      if (error) throw error;
      setMessagesUnreadCount(count || 0);
    } catch (error) {
      console.error('❌ ADMIN: Failed to load unread count:', error);
    }
  };

  const loadUserUnreadCounts = async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('sender_id')
        .eq('receiver_id', user.id)
        .is('read_at', null);
      
      if (error) throw error;
      
      const counts: Record<string, number> = {};
      data?.forEach(msg => {
        counts[msg.sender_id] = (counts[msg.sender_id] || 0) + 1;
      });
      
      setUserUnreadCounts(counts);
    } catch (error) {
      console.error('❌ ADMIN: Failed to load user unread counts:', error);
    }
  };

  useEffect(() => {
    const handleMessagesRead = () => {
      loadUnreadMessageCount();
    };

    const handleMessageReceived = () => {
      loadUnreadMessageCount();
    };

    window.addEventListener("messagesRead", handleMessagesRead);
    window.addEventListener("messageReceived", handleMessageReceived);

    return () => {
      window.removeEventListener("messagesRead", handleMessagesRead);
      window.removeEventListener("messageReceived", handleMessageReceived);
    };
  }, [user?.id]);

  const loadAdminData = async () => {
    setLoading(true);
    console.log('🔍 ADMIN: Starting to load ALL users from database...');
    
    try {
      // PRIMARY METHOD: Use Supabase RPC function to get all users
      console.log('🔍 ADMIN: Calling Supabase RPC get_all_users...');
      const { data: rpcUsers, error: rpcError } = await supabase.rpc('get_all_users');
      console.log('🔍 ADMIN: RPC response:', { data: rpcUsers, error: rpcError });
      
      if (!rpcError && rpcUsers && rpcUsers.length > 0) {
        console.log('✅ ADMIN: RPC returned users:', rpcUsers);
        const allUsers = rpcUsers.map((u: any) => ({
          id: u.id,
          display_name: u.display_name || u.email?.split('@')[0] || 'Unknown User',
          email: u.email || '',
          phone: u.phone || '',
          created_at: u.created_at,
          last_seen: u.last_seen || new Date().toISOString(),
          status: (u.banned_until && new Date(u.banned_until) > new Date()) ? 'blocked' : 'active' as const,
          notification_preferences: { email: true, sms: true, push: true }
        }));
        
        console.log('🎯 ADMIN: Final users array:', allUsers);
        console.log('📊 ADMIN: Total users found:', allUsers.length);
        
        setUsers(allUsers);
        setStats({
          totalUsers: allUsers.length,
          activeUsers: allUsers.length,
          blockedUsers: 0,
          totalNotifications: 47,
          systemHealth: 'healthy'
        });
        
        setLogs([
          {
            id: '1',
            level: 'info',
            message: `Loaded ${allUsers.length} real users from auth.users table`,
            timestamp: new Date().toISOString()
          },
          {
            id: '2',
            level: 'info',
            message: 'Admin dashboard accessed',
            timestamp: new Date(Date.now() - 1800000).toISOString()
          }
        ]);
      } else {
        console.log('❌ ADMIN: RPC failed:', rpcError);
        setUsers([]);
        setStats({
          totalUsers: 0,
          activeUsers: 0,
          blockedUsers: 0,
          totalNotifications: 0,
          systemHealth: 'warning'
        });
        
        setLogs([
          {
            id: '1',
            level: 'error',
            message: `RPC function failed: ${rpcError?.message || 'Unknown error'}`,
            timestamp: new Date().toISOString()
          }
        ]);
      }
      
    } catch (error) {
      console.error('💥 ADMIN: Critical error loading admin data:', error);
      setUsers([]);
      setStats({
        totalUsers: 0,
        activeUsers: 0,
        blockedUsers: 0,
        totalNotifications: 0,
        systemHealth: 'critical'
      });
    } finally {
      setLoading(false);
      console.log('🏁 ADMIN: Load admin data completed');
    }
  };

  const toggleUserStatus = async (userId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'active' ? 'blocked' : 'active';
      console.log(`🔄 ADMIN: Toggling user ${userId} from ${currentStatus} to ${newStatus}`);
      
      // Use RPC function to toggle user status
      const { data, error } = await supabase.rpc('toggle_user_status', {
        target_user_id: userId,
        new_status: newStatus
      });
      
      if (error) {
        console.error('❌ ADMIN: RPC toggle_user_status failed:', error);
        throw error;
      }
      
      console.log('✅ ADMIN: User status updated successfully');
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, status: newStatus as 'active' | 'blocked' } : u
      ));
      
      // Update stats
      setStats(prev => ({
        ...prev,
        activeUsers: newStatus === 'active' ? prev.activeUsers + 1 : prev.activeUsers - 1,
        blockedUsers: newStatus === 'blocked' ? prev.blockedUsers + 1 : prev.blockedUsers - 1
      }));
      
    } catch (error) {
      console.error('💥 ADMIN: Failed to update user status:', error);
      alert(`Failed to ${currentStatus === 'active' ? 'block' : 'unblock'} user. Please try again.`);
    }
  };

  const sendBulkNotification = async (toSelected = false) => {
    if (!bulkMessage.trim()) {
      setBulkResult('Please enter a message');
      return;
    }
    
    if (toSelected && selectedUsers.length === 0) {
      setBulkResult('Please select at least one user');
      return;
    }
    
    setSendingBulk(true);
    setBulkResult('');
    
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      
      const response = await fetch(`${backendUrl}/api/admin/notifications/bulk`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'user-id': user?.id || ''
        },
        body: JSON.stringify({
          message: bulkMessage,
          recipients: toSelected ? selectedUsers : 'all',
          type: 'admin_announcement'
        })
      });

      if (response.ok) {
        const result = await response.json();
        setBulkResult(`✅ Notification sent to ${result.sent} users successfully!`);
        setBulkMessage('');
        setSelectedUsers([]);
        setShowUserSelection(false);
      } else {
        setBulkResult('❌ Failed to send notification');
      }
    } catch (error) {
      console.error('Failed to send bulk notification:', error);
      setBulkResult('❌ Failed to send notification');
    } finally {
      setSendingBulk(false);
    }
  };
  
  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const loadMessagesForFarmer = async (farmerId: string) => {
    setLoadingMessages(true);
    try {
      console.log('🔄 ADMIN: Loading messages for farmer:', farmerId);
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(`and(sender_id.eq.${user.id},receiver_id.eq.${farmerId}),and(sender_id.eq.${farmerId},receiver_id.eq.${user.id})`)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      console.log('✅ ADMIN: Loaded messages from DB:', data?.length || 0, 'messages');
      setAdminMessages(data || []);
    } catch (error) {
      console.error('❌ ADMIN: Failed to load messages:', error);
      setAdminMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendAdminMessage = async () => {
    console.log('🔄 ADMIN: Send button clicked');
    console.log('Admin message:', adminMessage);
    console.log('Selected farmer:', selectedFarmer);
    console.log('User ID:', user?.id);
    
    if (!adminMessage.trim()) {
      console.log('❌ ADMIN: No message text');
      return;
    }
    
    if (!selectedFarmer) {
      console.log('❌ ADMIN: No farmer selected');
      return;
    }
    
    try {
      console.log(`📤 ADMIN: Sending message to farmer ${selectedFarmer}:`, adminMessage);
      
      const { data, error } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          receiver_id: selectedFarmer,
          message: adminMessage.trim(),
          parent_message_id: replyingTo?.id || null
        })
        .select()
        .single();
      
      if (error) {
        console.error('❌ ADMIN: Database error:', error);
        throw error;
      }
      
      console.log('✅ ADMIN: Message sent to database:', data);
      
      // Clear input immediately
      setAdminMessage('');
      setReplyingTo(null);
      
      // Reload messages and update count
      await loadMessagesForFarmer(selectedFarmer);
      await loadUnreadMessageCount();
      
      // Notify other components
      window.dispatchEvent(new CustomEvent('messageReceived'));
    } catch (error) {
      console.error('❌ ADMIN: Failed to send message:', error);
    }
  };

  const handleEditMessage = async (messageId: string, newText: string) => {
    try {
      const { error } = await supabase
        .from('messages')
        .update({ 
          message: newText,
          edited_at: new Date().toISOString()
        })
        .eq('id', messageId);
      
      if (error) throw error;
      
      // Update local state
      setAdminMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, message: newText, edited_at: new Date().toISOString() }
          : msg
      ));
      setEditingMessage(null);
      setEditText('');
    } catch (error) {
      console.error('❌ ADMIN: Failed to edit message:', error);
      alert('Failed to edit message. Please try again.');
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      console.log('🗑️ ADMIN: Deleting message:', messageId);
      
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId);
      
      if (error) {
        console.error('❌ ADMIN: Delete error:', error);
        throw error;
      }
      
      console.log('✅ ADMIN: Message deleted from database');
      
      // Reload messages from database to ensure consistency
      if (selectedFarmer) {
        await loadMessagesForFarmer(selectedFarmer);
      }
      
      setShowDeleteModal(false);
      setMessageToDelete(null);
    } catch (error) {
      console.error('❌ ADMIN: Failed to delete message:', error);
    }
  };

  const handleLikeMessage = async (messageId: string) => {
    try {
      // Toggle like status in local state immediately for better UX
      setAdminMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, liked: !msg.liked }
          : msg
      ));
      console.log('❤️ ADMIN: Message liked');
    } catch (error) {
      console.error('❌ ADMIN: Failed to like message:', error);
    }
  };

  const handleSelectFarmer = async (farmerId: string) => {
    console.log('👨‍🌾 ADMIN: Selecting farmer:', farmerId);
    setSelectedFarmer(farmerId);
    await loadMessagesForFarmer(farmerId);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const StatCard = ({ icon: Icon, title, value, color, trend }: any) => (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {trend && (
            <p className={`text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? '+' : ''}{trend}% from last week
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (!user || user.email !== 'kutloano.moshao111@gmail.com') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t("admin.accessDenied")}</h1>
          <p className="text-gray-600">{t("admin.noPrivileges")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col pt-20 bg-green-50" role="main">
      {/* Welcome Header */}
      <div className="flex justify-start px-2 pt-3 mb-1 sm:px-4">
        <span className="inline-flex items-center px-2 py-3 text-xs font-semibold text-green-800 rounded sm:text-sm">
          {t("admin.welcome")}, {adminDisplayName}
        </span>
      </div>

      {/* App Summary */}
      <div className="mt-6 mb-8 text-center">
        <h2 className="mb-2 text-2xl font-bold text-green-800">
          {t("admin.dashboard")}
        </h2>
        <p className="max-w-2xl mx-auto text-green-800">
          {t("admin.manageUsers")}
        </p>
        <button
          onClick={loadAdminData}
          className="mt-4 flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 mx-auto"
        >
          <RefreshCw className="w-4 h-4" />
          {t("admin.refresh")}
        </button>
      </div>

      {/* Fixed Tabs - Always visible */}
      <div className="sticky top-0 z-10 mb-4 mt-8 bg-white">
        <div className="px-2 sm:px-4">
          <div className="flex justify-start w-full p-0 overflow-x-auto scrollbar-hide">
            {[
              { id: 'overview', label: t('admin.overview'), icon: BarChart3 },
              { id: 'users', label: t('admin.users'), icon: Users },
              { id: 'messages', label: `Messages${messagesUnreadCount > 0 ? ` (${messagesUnreadCount > 99 ? '99+' : messagesUnreadCount})` : ''}`, icon: MessageSquare },
              { id: 'notifications', label: t('admin.notifications'), icon: Bell },
              { id: 'logs', label: t('admin.logs'), icon: Activity },
              { id: 'settings', label: t('admin.settings'), icon: Settings }
            ].map(tab => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 whitespace-nowrap px-3 sm:px-6 h-12 text-sm font-medium transition-all relative flex items-center bg-white border-0 ${
                  activeTab === tab.id
                    ? "text-green-700 border-b-2 border-green-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-2 pb-8 sm:px-4">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6">

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">{t("admin.loadingData")}</p>
              </div>
            ) : (
              <>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div role="tabpanel" id="tabpanel-0" aria-labelledby="tab-0">
              <div className="space-y-8">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <StatCard
                    icon={Users}
                    title={t("admin.totalUsers")}
                    value={stats.totalUsers}
                    color="bg-blue-500"
                    trend={12}
                  />
                  <StatCard
                    icon={Activity}
                    title={t("admin.activeUsers")}
                    value={stats.activeUsers}
                    color="bg-green-500"
                    trend={8}
                  />
                  <StatCard
                    icon={Ban}
                    title={t("admin.blockedUsers")}
                    value={stats.blockedUsers}
                    color="bg-red-500"
                    trend={-2}
                  />
                  <StatCard
                    icon={MessageSquare}
                    title={t("admin.notificationsSent")}
                    value={stats.totalNotifications}
                    color="bg-purple-500"
                    trend={25}
                  />
                </div>

                {/* System Health */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("admin.systemHealth")}</h3>
                  <div className="flex items-center gap-4">
                    <div className={`w-4 h-4 rounded-full ${
                      stats.systemHealth === 'healthy' ? 'bg-green-500' :
                      stats.systemHealth === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                    }`}></div>
                    <span className="text-gray-700 capitalize">{t(`admin.${stats.systemHealth}`)}</span>
                  </div>
                </div>
                  </div>
                  </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                  <div role="tabpanel" id="tabpanel-1" aria-labelledby="tab-1">
              <div className="space-y-6">
                {/* Search and Filter */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder={t("admin.searchUsers")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="all">{t("admin.allUsers")}</option>
                      <option value="active">{t("admin.active")}</option>
                      <option value="blocked">{t("admin.blocked")}</option>
                    </select>
                  </div>
                </div>

                {/* Users Table - Desktop */}
                <div className="hidden lg:block bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.user")}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.contact")}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.status")}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.notifications")}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("admin.actions")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredUsers.map(user => (
                          <tr key={user.id}>
                            <td className="px-6 py-4">
                              <div>
                                <div className="font-medium text-gray-900">{user.display_name}</div>
                                <div className="text-sm text-gray-500">{t("admin.joined")} {new Date(user.created_at).toLocaleDateString()}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">{user.email}</div>
                              <div className="text-sm text-gray-500">{user.phone}</div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                user.status === 'active' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {user.status}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex gap-2">
                                {user.notification_preferences?.email && <Mail className="w-4 h-4 text-blue-500" />}
                                {user.notification_preferences?.sms && <Phone className="w-4 h-4 text-green-500" />}
                                {user.notification_preferences?.push && <Bell className="w-4 h-4 text-purple-500" />}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => toggleUserStatus(user.id, user.status)}
                                className={`px-3 py-1 text-xs font-medium rounded-full ${
                                  user.status === 'active'
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                                }`}
                              >
                                {user.status === 'active' ? t('admin.block') : t('admin.unblock')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Users Cards - Mobile */}
                <div className="lg:hidden space-y-4">
                  {filteredUsers.map(user => (
                    <div key={user.id} className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{user.display_name}</h4>
                          <p className="text-sm text-gray-600">{user.email}</p>
                          <p className="text-sm text-gray-500">{user.phone}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          user.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.status}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2">
                          {user.notification_preferences?.email && <Mail className="w-4 h-4 text-blue-500" />}
                          {user.notification_preferences?.sms && <Phone className="w-4 h-4 text-green-500" />}
                          {user.notification_preferences?.push && <Bell className="w-4 h-4 text-purple-500" />}
                        </div>
                        
                        <button
                          onClick={() => toggleUserStatus(user.id, user.status)}
                          className={`px-3 py-1 text-xs font-medium rounded-full ${
                            user.status === 'active'
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {user.status === 'active' ? t('admin.block') : t('admin.unblock')}
                        </button>
                      </div>
                      
                      <div className="mt-2 text-xs text-gray-500">
                        {t("admin.joined")} {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
                  </div>
                  </div>
                )}

                {/* Messages Tab */}
                {activeTab === 'messages' && (
                  <div role="tabpanel" id="tabpanel-2" aria-labelledby="tab-2">
                    <div className="h-full flex flex-col">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Admin Messages</h3>
                          <p className="text-sm text-gray-600">Chat with farmers and provide support</p>
                        </div>
                      </div>

                      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Farmers List */}
                        <div className="lg:col-span-1">
                          <h4 className="font-medium text-gray-900 mb-3">Farmers ({users.length})</h4>
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {users.map((farmer) => (
                              <button
                                key={farmer.id}
                                onClick={async () => {
                                  setSelectedFarmer(farmer.id);
                                  await loadMessagesForFarmer(farmer.id);
                                  
                                  // Mark messages as read when opening chat
                                  try {
                                    const { error } = await supabase
                                      .from('messages')
                                      .update({ read_at: new Date().toISOString() })
                                      .eq('sender_id', farmer.id)
                                      .eq('receiver_id', user.id)
                                      .is('read_at', null);
                                    
                                    if (error) throw error;
                                    
                                    // Update counts
                                    await loadUnreadMessageCount();
                                    await loadUserUnreadCounts();
                                  } catch (error) {
                                    console.error('❌ ADMIN: Failed to mark messages as read:', error);
                                  }
                                }}
                                className={`w-full text-left p-3 rounded-lg border transition-all duration-200 ${
                                  selectedFarmer === farmer.id
                                    ? 'bg-green-50 border-green-200 shadow-sm'
                                    : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="relative">
                                      <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                                        <Users className="w-5 h-5 text-gray-400" />
                                      </div>
                                      <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                                        farmer.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                                      }`}></div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-sm text-gray-900 truncate">
                                        {farmer.display_name}
                                      </p>
                                      <p className="text-xs text-gray-500 truncate">
                                        {farmer.email}
                                      </p>
                                    </div>
                                    {userUnreadCounts[farmer.id] > 0 && (
                                      <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                                        {userUnreadCounts[farmer.id] > 99 ? '99+' : userUnreadCounts[farmer.id]}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="mt-2 pl-13">
                                  <p className="text-xs text-gray-400">
                                    {farmer.status === 'active' ? 'Active' : 'Blocked'} • Joined {new Date(farmer.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </button>
                            ))}
                            
                            {users.length === 0 && (
                              <div className="text-center py-8 text-gray-500">
                                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p className="text-sm">No farmers found</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Chat Area */}
                        <div className="lg:col-span-2">
                          {selectedFarmer ? (
                            <div className="border border-gray-200 rounded-lg h-full flex flex-col">
                              {/* Chat Header */}
                              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                                <div className="flex items-center gap-3">
                                  <div className="relative">
                                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                                      <Users className="w-5 h-5 text-gray-400" />
                                    </div>
                                    <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                                      users.find(u => u.id === selectedFarmer)?.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                                    }`}></div>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-gray-900">
                                      {users.find(u => u.id === selectedFarmer)?.display_name || 'Farmer'}
                                    </h4>
                                    <p className="text-sm text-gray-500">
                                      {users.find(u => u.id === selectedFarmer)?.status === 'active' ? 'Active' : 'Blocked'}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Messages */}
                              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {loadingMessages ? (
                                  <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                                    <p className="text-sm text-gray-500 mt-2">Loading messages...</p>
                                  </div>
                                ) : adminMessages.length > 0 ? (
                                  adminMessages.map((message) => {
                                    const isSender = message.sender_id === user.id;
                                    const isEditing = editingMessage === message.id;
                                    return (
                                      <div key={message.id} className={`flex ${isSender ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-xs lg:max-w-md relative`}>
                                          {/* Reply indicator */}
                                          {message.parent_message_id && (
                                            <div className="text-xs text-gray-500 mb-1 pl-3 border-l-2 border-gray-300">
                                              Replying to previous message
                                            </div>
                                          )}
                                          
                                          <div className={`px-4 py-2 rounded-lg ${
                                            isSender
                                              ? 'bg-green-600 text-white'
                                              : 'bg-gray-100 text-gray-900'
                                          }`}>
                                            {isEditing ? (
                                              <div className="space-y-2">
                                                <input
                                                  type="text"
                                                  value={editText}
                                                  onChange={(e) => setEditText(e.target.value)}
                                                  className="w-full p-2 text-sm border rounded text-gray-900"
                                                  onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                      handleEditMessage(message.id, editText);
                                                    }
                                                  }}
                                                  autoFocus
                                                />
                                                <div className="flex gap-2">
                                                  <button
                                                    onClick={() => handleEditMessage(message.id, editText)}
                                                    className="px-2 py-1 bg-green-500 text-white rounded text-xs"
                                                  >
                                                    Save
                                                  </button>
                                                  <button
                                                    onClick={() => {
                                                      setEditingMessage(null);
                                                      setEditText('');
                                                    }}
                                                    className="px-2 py-1 bg-gray-500 text-white rounded text-xs"
                                                  >
                                                    Cancel
                                                  </button>
                                                </div>
                                              </div>
                                            ) : (
                                              <>
                                                <p className="text-sm">{message.message}</p>
                                                <div className="flex items-center justify-between mt-1">
                                                  <p className={`text-xs ${
                                                    isSender ? 'text-green-100' : 'text-gray-500'
                                                  }`}>
                                                    {new Date(message.created_at).toLocaleTimeString([], {
                                                      hour: '2-digit',
                                                      minute: '2-digit'
                                                    })}
                                                    {message.edited_at && ' (edited)'}
                                                  </p>
                                                  {message.liked && (
                                                    <Heart className="w-3 h-3 text-red-500" fill="currentColor" />
                                                  )}
                                                </div>
                                              </>
                                            )}
                                          </div>
                                          
                                          {/* Message Actions */}
                                          {!isEditing && (
                                            <div className={`flex items-center gap-1 mt-1 ${
                                              isSender ? 'justify-end' : 'justify-start'
                                            }`}>
                                              <button
                                                onClick={() => handleLikeMessage(message.id)}
                                                className={`p-1 rounded-full hover:bg-gray-200 transition-colors ${
                                                  message.liked ? 'text-red-500' : 'text-gray-500'
                                                }`}
                                                title="Like"
                                              >
                                                <Heart className="w-3 h-3" fill={message.liked ? 'currentColor' : 'none'} />
                                              </button>
                                              
                                              <button
                                                onClick={() => setReplyingTo(message)}
                                                className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
                                                title="Reply"
                                              >
                                                <Reply className="w-3 h-3" />
                                              </button>
                                              
                                              {isSender && (
                                                <>
                                                  <button
                                                    onClick={() => {
                                                      setEditingMessage(message.id);
                                                      setEditText(message.message);
                                                    }}
                                                    className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
                                                    title="Edit"
                                                  >
                                                    <Edit3 className="w-3 h-3" />
                                                  </button>
                                                  
                                                  <button
                                                    onClick={() => {
                                                      setMessageToDelete(message.id);
                                                      setShowDeleteModal(true);
                                                    }}
                                                    className="p-1 rounded-full hover:bg-gray-200 transition-colors text-red-500"
                                                    title="Delete"
                                                  >
                                                    <Trash2 className="w-3 h-3" />
                                                  </button>
                                                </>
                                              )}
                                            </div>
                                          )}
                                          
                                          {/* Delete Confirmation Modal */}
                                          {showDeleteModal && messageToDelete === message.id && (
                                            <div className="absolute top-8 right-0 bg-white border rounded-lg shadow-lg p-3 z-10 min-w-48">
                                              <p className="text-sm text-gray-700 mb-3">Delete this message?</p>
                                              <div className="flex gap-2">
                                                <button
                                                  onClick={() => {
                                                    setShowDeleteModal(false);
                                                    setMessageToDelete(null);
                                                  }}
                                                  className="px-3 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                                                >
                                                  Cancel
                                                </button>
                                                <button
                                                  onClick={() => handleDeleteMessage(message.id)}
                                                  className="px-3 py-1 text-xs text-white bg-red-600 rounded hover:bg-red-700"
                                                >
                                                  Delete
                                                </button>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-8 text-gray-500">
                                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                    <p className="text-sm">Start a conversation with {users.find(u => u.id === selectedFarmer)?.display_name}</p>
                                  </div>
                                )}
                              </div>

                              {/* Reply indicator */}
                              {replyingTo && (
                                <div className="px-4 py-2 bg-blue-50 border-t border-blue-200">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Reply className="w-4 h-4 text-blue-600" />
                                      <span className="text-sm text-blue-800">
                                        Replying to: {replyingTo.message.substring(0, 50)}...
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => setReplyingTo(null)}
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Message Input */}
                              <div className="p-4 border-t border-gray-200 bg-gray-50">
                                <div className="flex gap-3">
                                  <input
                                    type="text"
                                    value={adminMessage}
                                    onChange={(e) => setAdminMessage(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSendAdminMessage()}
                                    placeholder={replyingTo ? `Replying to ${users.find(u => u.id === selectedFarmer)?.display_name}...` : "Type your message to farmer..."}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                  />
                                  
                                  <button
                                    onClick={() => {
                                      console.log('💆 ADMIN: Send button clicked directly');
                                      handleSendAdminMessage();
                                    }}
                                    disabled={!adminMessage.trim()}
                                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2 font-medium"
                                  >
                                    <MessageSquare className="w-4 h-4" />
                                    Send
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center h-full border border-gray-200 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100">
                              <div className="text-center text-gray-500">
                                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                                  <MessageSquare className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="font-medium text-gray-700 mb-2">No Farmer Selected</h3>
                                <p className="text-sm">Choose a farmer from the list to start messaging</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}



                {/* Notifications Tab */}
                {activeTab === 'notifications' && (
                  <div role="tabpanel" id="tabpanel-3" aria-labelledby="tab-3">
              <div className="space-y-6">
                <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("admin.sendBulkNotification")}</h3>
                  <div className="space-y-4">
                    <textarea
                      value={bulkMessage}
                      onChange={(e) => setBulkMessage(e.target.value)}
                      placeholder={t("admin.enterMessage")}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm sm:text-base"
                      rows={4}
                    />
                    <div className="flex flex-col sm:flex-row gap-4">
                      <button 
                        onClick={() => sendBulkNotification(false)}
                        disabled={sendingBulk || !bulkMessage.trim()}
                        className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {sendingBulk ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            {t("admin.sending")}
                          </>
                        ) : (
                          <>
                            <MessageSquare className="w-4 h-4" />
                            {t("admin.sendToAll")} ({stats.activeUsers})
                          </>
                        )}
                      </button>
                      
                      <button 
                        onClick={() => setShowUserSelection(!showUserSelection)}
                        disabled={sendingBulk}
                        className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <Users className="w-4 h-4" />
                        {t("admin.selectUsers")} ({selectedUsers.length})
                      </button>
                    </div>
                    
                    {showUserSelection && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-3">{t("admin.selectUsersToNotify")}</h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {users.map(user => (
                            <label key={user.id} className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => toggleUserSelection(user.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <span className="text-sm font-medium text-gray-900">{user.display_name}</span>
                                <span className="text-xs text-gray-500 ml-2">({user.email})</span>
                              </div>
                            </label>
                          ))}
                        </div>
                        
                        <div className="mt-4 flex gap-2">
                          <button 
                            onClick={() => sendBulkNotification(true)}
                            disabled={sendingBulk || !bulkMessage.trim() || selectedUsers.length === 0}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          >
                            {t("admin.sendToSelected")} ({selectedUsers.length})
                          </button>
                          <button 
                            onClick={() => setSelectedUsers(users.map(u => u.id))}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                          >
                            {t("admin.selectAll")}
                          </button>
                          <button 
                            onClick={() => setSelectedUsers([])}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                          >
                            {t("admin.clear")}
                          </button>
                        </div>
                      </div>
                    )}
                    {bulkResult && (
                      <div className={`p-3 rounded-lg text-sm ${
                        bulkResult.includes('✅') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {bulkResult}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Recent Notifications */}
                <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("admin.recentNotifications")}</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{t("admin.weatherAlertSent")}</p>
                        <p className="text-sm text-gray-600">{t("admin.sentToActiveUsers", { count: stats.activeUsers })}</p>
                      </div>
                      <span className="text-xs text-gray-500">{t("admin.hoursAgo", { count: 2 })}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{t("admin.systemMaintenanceNotice")}</p>
                        <p className="text-sm text-gray-600">{t("admin.sentToAllUsers")}</p>
                      </div>
                      <span className="text-xs text-gray-500">{t("admin.dayAgo", { count: 1 })}</span>
                    </div>
                  </div>
                </div>
                  </div>
                  </div>
                )}

                {/* Logs Tab */}
                {activeTab === 'logs' && (
                  <div role="tabpanel" id="tabpanel-4" aria-labelledby="tab-4">
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="p-4 sm:p-6 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <h3 className="text-lg font-semibold text-gray-900">{t("admin.systemLogs")}</h3>
                    <select 
                      value={logFilter}
                      onChange={(e) => {
                        setLogFilter(e.target.value);
                        loadAdminData();
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                    >
                      <option value="all">{t("admin.allLevels")}</option>
                      <option value="info">{t("admin.info")}</option>
                      <option value="warning">{t("admin.warning")}</option>
                      <option value="error">{t("admin.error")}</option>
                    </select>
                  </div>
                </div>
                <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                  {logs.length > 0 ? logs.map(log => (
                    <div key={log.id} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                          log.level === 'error' ? 'bg-red-500' :
                          log.level === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
                        }`}></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 break-words">{log.message}</span>
                            <span className={`px-2 py-1 text-xs rounded-full self-start sm:self-auto ${
                              log.level === 'error' ? 'bg-red-100 text-red-700' :
                              log.level === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {log.level}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{new Date(log.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="p-8 text-center text-gray-500">
                      <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>{t("admin.noLogsAvailable")}</p>
                    </div>
                  )}
                </div>
                  </div>
                  </div>
                )}
            
                {/* Settings Tab */}
                {activeTab === 'settings' && (
                  <div role="tabpanel" id="tabpanel-5" aria-labelledby="tab-5">
              <div className="space-y-6">
                {/* System Settings */}
                <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("admin.systemSettings")}</h3>
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900">{t("admin.automatedNotifications")}</h4>
                        <p className="text-sm text-gray-600">{t("admin.dailyWeatherAlerts")}</p>
                      </div>
                      <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                        {t("admin.enabled")}
                      </button>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900">{t("admin.userRegistration")}</h4>
                        <p className="text-sm text-gray-600">{t("admin.allowNewFarmers")}</p>
                      </div>
                      <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                        {t("admin.enabled")}
                      </button>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900">{t("admin.maintenanceMode")}</h4>
                        <p className="text-sm text-gray-600">{t("admin.temporarilyDisable")}</p>
                      </div>
                      <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">
                        {t("admin.disabled")}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Admin Settings */}
                <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("admin.adminSettings")}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t("admin.adminEmail")}</label>
                      <input 
                        type="email" 
                        value={user?.email || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t("admin.displayName")}</label>
                      <input 
                        type="text" 
                        value={adminDisplayName}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm"
                      />
                    </div>
                    
                    <div className="pt-4">
                      <button className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                        {t("admin.clearSystemCache")}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Database Stats */}
                <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("admin.databaseStatistics")}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">{t("admin.totalRecords")}</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.totalUsers * 10}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">{t("admin.storageUsed")}</p>
                      <p className="text-2xl font-bold text-gray-900">2.4 GB</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">{t("admin.apiCallsToday")}</p>
                      <p className="text-2xl font-bold text-gray-900">1,247</p>
                    </div>
                  </div>
                </div>
                  </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}